import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import categoriesData from "../../../data/categories.json"; // Path to categories.json
import {
  createBrand,
  getCompanyProfile,
} from "../../services/companyAuthService";
import { useNavigate } from "react-router-dom";
import countriesData from "../../../data/countries.json";

const CreateBrand = () => {
  const [brandName, setBrandName] = useState("");
  const [country, setCountry] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [allCountries, setAllCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brandCreated, setBrandCreated] = useState(false);

  // const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract country names and sort them alphabetically
    const countryNames = countriesData
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));

    setAllCountries(countryNames);
    setCategories(categoriesData);
  }, []);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await getCompanyProfile();
        if (response.success) {
          setCompanyName(response.company.companyName);
        } else {
          toast.error("Failed to load company profile");
        }
      } catch (error) {
        console.error("Error fetching company profile:", error);
        toast.error(error.message || "Failed to load company profile");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchCompanyProfile();
  }, []);

  // Filter countries based on user input
  const handleCountryChange = (e) => {
    const value = e.target.value;
    setCountry(value);

    if (value === "") {
      setFilteredCountries([]);
      setShowCountryDropdown(false);
    } else {
      const filtered = allCountries.filter((c) =>
        c.toLowerCase().startsWith(value.toLowerCase())
      );
      setFilteredCountries(filtered);
      setShowCountryDropdown(filtered.length > 0);
    }
  };

  // Select country from dropdown
  const handleSelectCountry = (selectedCountry) => {
    setCountry(selectedCountry);
    setFilteredCountries([]);
    setShowCountryDropdown(false);
  };

  // Handle category change
  const handleCategoryChange = (e) => {
    const selectedCategoryName = e.target.value;
    setSelectedCategory(selectedCategoryName);

    // Get subcategories based on selected category
    const category = categories.find(
      (cat) => cat.category === selectedCategoryName
    );
    setSubcategories(category ? category.subcategories : []);
    setSelectedSubcategory("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (
      !brandName ||
      !country ||
      !selectedCategory ||
      !selectedSubcategory ||
      !companyName
    ) {
      toast.error("All fields are required");
      return;
    }

    try {
      setIsSubmitting(true);

      const brandData = {
        brandName,
        country,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        companyName,
      };

      const response = await createBrand(brandData);

      if (response.success) {
        toast.success("Brand created successfully!");
        setBrandCreated(true); // ✅ Show buttons instead of redirecting
      } else {
        toast.error("Failed to create brand");
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      toast.error(error.message || "Failed to create brand");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      {/* Back Button */}
      <div>
        <button
          onClick={() => navigate("/company/dashboard/brands")}
          className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
        >
          ← Back
        </button>
      </div>
      {/* Navbar outside the main wrapper */}
      <div className="h-100 bg-gray-100 flex lg:items-center justify-center px-2">
        <div className="bg-white w-full max-w-4xl p-6 sm:p-8 lg:p-10 rounded-xl shadow-md mt-6">


          {brandCreated ? (
            <h2 className="text-2xl font-bold text-black text-center mb-6">
              Brand Created Successfully!
            </h2>
          ) : (
            <h2 className="text-2xl font-bold text-black text-center mb-6">
              Create a New Brand
            </h2>
          )}
          {!brandCreated ? (
            // Existing form
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Brand Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Enter your brand name"
                  className="w-full border rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
                  required
                />
              </div>

              {/* Select Country */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={country}
                  onChange={handleCountryChange}
                  placeholder="Search and select country"
                  className="w-full border rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
                  required
                />
                {showCountryDropdown && (
                  <ul className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-md mt-1 w-full max-h-40 overflow-y-auto">
                    {filteredCountries.map((c, index) => (
                      <li
                        key={index}
                        onClick={() => handleSelectCountry(c)}
                        className="p-2 hover:bg-orange-100 cursor-pointer text-sm"
                      >
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Select Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="w-full border rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
                  required
                  style={{ maxHeight: "30vh", overflowY: "auto" }}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.category} value={cat.category}>
                      {cat.category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Subcategory */}
              {selectedCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Subcategory <span className="text-red-500">*</span>
                  </label>
                  {selectedCategory === "Other" ? (
                    <input
                      type="text"
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      placeholder="Enter subcategory"
                      className="w-full border rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
                      required
                    />
                  ) : (
                    <select
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      className="w-full border rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
                      required
                      style={{ maxHeight: "30vh", overflowY: "auto" }}
                    >
                      <option value="">
                        {selectedCategory === "Other"
                          ? "Enter Subcategory"
                          : "Select Subcategory"}
                      </option>
                      {subcategories.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  disabled
                  className="w-full border rounded-md p-2 mt-1 bg-gray-50 cursor-not-allowed"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/company/dashboard/brands")}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-all duration-300 font-medium ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  {isSubmitting ? "Creating..." : "Create Brand"}
                </button>
              </div>

            </form>
          ) : (
            // ✅ Show buttons after success
            <div className="text-center space-y-6">
            <div className="flex flex-col items-center sm:flex-row sm:justify-center gap-4">
              <button
                onClick={() => navigate("/company/dashboard/brands/")}
                className="w-full max-w-xs sm:w-48 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition"
              >
                Your Brands
              </button>
          
              <button
                onClick={() => navigate("/company/dashboard/brands/brand-managers")}
                className="w-full max-w-xs sm:w-48 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition"
              >
                Add Brand Manager
              </button>
          
              <button
                onClick={() => navigate("/company/dashboard/campaigns/create")}
                className="w-full max-w-xs sm:w-48 bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition font-semibold"
              >
                Continue Integration
              </button>
            </div>
          </div>
          
          )}
        </div>
      </div>
      {/* Toastify Container */}
      <ToastContainer />
    </>
  );
};

export default CreateBrand;
